// authInit.ts
import { Hub } from 'aws-amplify/utils';
import dayjs from 'dayjs';
import { LOGIN_SUCCESS, LOGOUT } from './redux/actions/types';
import { setAuthSuccess, setAuthProgress, setAuthFailed } from './redux/reducers/user';
import namedBaselines from './misc/namedBaselines';
import i18n from './i18n';
import { getAuthenticatedUser } from './amplify/authUtilities';

const debugLevel = Number(process.env.REACT_APP_DEBUG_LEVEL) || 0;

// take whatever currentAuthenticatedUser returns and update the store with it
export function updateStoreWithAttributes(user) {
  if (debugLevel > 2) console.info('Hub Listener: updateStoreWithAttributes(user)', user);

  const attributes = user?.attributes || {};

  // prepare default settings
  const defaultBaselineDate = {
    name: 'sameTimeLastYear',
    date: namedBaselines.sameTimeLastYear,
  };

  const settingsString = attributes['custom:settings'];
  const settings = settingsString
    ? JSON.parse(settingsString)
    : {
      baseCurrency: 'EUR',
      baselineDate: defaultBaselineDate.date,
      namedBaseline: defaultBaselineDate.name,
      inflationRate: 3.0,
      sliderStartDate: dayjs().subtract(10, 'year').startOf('year').valueOf(),
      sliderEndDate: dayjs().add(20, 'year').startOf('year').valueOf(),
      expectedDateFormats: ['DD.MM.YYYY', 'YYYY-MM-DD', 'DD.MM.YY', 'DD/MM/YYYY', 'DD-MM-YYYY'],
    };

  const locale = attributes.locale || null;
  if (locale !== i18n.language && locale?.length === 2) {
    i18n.changeLanguage(locale);
  }

  const { sub = null } = attributes;

  return {
    toursSeen: JSON.parse(attributes['custom:tours_seen'] || 'null'),
    settings,
    locale,
    sub,
  };
}

export default async function initAuth(dispatch) {
  try {
    dispatch(setAuthProgress()); // marks that initial check is in progress

    const user = await getAuthenticatedUser();
    const payload = updateStoreWithAttributes(user);
    if (debugLevel > 1) console.log('initAuth: current authenticated user', user);

    dispatch({
      type: LOGIN_SUCCESS,
      payload,
    });
    dispatch(setAuthSuccess());
  } catch (err) {
    dispatch(setAuthFailed()); // marks that initial check finished
    dispatch({
      type: LOGOUT,
    });
  }

  // handle auth events
  return Hub.listen('auth', async ({ payload }) => {
    switch (payload.event) {
      case 'signedIn':
      case 'tokenRefresh': {
        try {
          const userData = payload?.data?.attributes ? payload.data : await getAuthenticatedUser();
          dispatch({
            type: LOGIN_SUCCESS,
            payload: updateStoreWithAttributes(userData),
          });
          dispatch(setAuthSuccess());
        } catch (error) {
          console.error('initAuth: failed to refresh user data after auth event', error);
          dispatch(setAuthFailed());
          dispatch({
            type: LOGOUT,
          });
        }
        break;
      }
      case 'signedOut':
      case 'tokenRefresh_failure':
      default:
        dispatch({
          type: LOGOUT,
        });
        break;
    }
  });
}
